import { useAtomValue } from 'jotai';
import { useMemo } from 'react';

import { mapLineChartData } from '../../../../../components/Charts/LineChart/mapLineChartData';

import { transactionsToCashflows } from '@foresightdata/metrics-calculator';
import { MetricsTransactionDataModel } from '../../../../../schemas/MetricsTransaction.schema';
import { LineChartData, TQuoter } from '../../../../../types';
import { startOfDay } from 'date-fns';
import { calculatePoolIrr } from '../../../providers/calculatePoolIrr';
import { QuartersBuilder } from '../../../services/QuartersBuilder';
import { metricsFilteredByViewAtom } from '../../../state/MetricsStateJ';
import { selectedViewDatePFJ } from '../../../state/PageStateJ';
import { selectedViewIdPFJ } from '../../../state/ViewStateJ';

type TQuarterTransactions = Record<string, MetricsTransactionDataModel[]>;

interface IrrByGroup {
  data: LineChartData[];
  last12Quarters: TQuoter[];
}

export function useIrrOverall(): IrrByGroup {
  const viewId = useAtomValue(selectedViewIdPFJ);
  const rawMetricsData = useAtomValue(metricsFilteredByViewAtom(viewId));
  const date = useAtomValue(selectedViewDatePFJ);

  const last12Quarters = useMemo(
    () =>
      QuartersBuilder.last12Quarters(date ?? new Date()).sort(
        (a: TQuoter, b: TQuoter) => a.start.getTime() - b.start.getTime()
      ),
    [date]
  );

  const data = useMemo(() => {
    const dataset: TQuarterTransactions = getFundQuarters(last12Quarters);

    function getTransactionQuarters(
      transaction: MetricsTransactionDataModel,
      quarters: TQuoter[]
    ): TQuoter[] {
      if (!transaction.transactionDate) {
        return [];
      }

      const tDate = new Date(transaction.transactionDate);
      tDate.setHours(0);
      tDate.setMinutes(0);
      tDate.setSeconds(0);

      return quarters.filter((q) => tDate <= q.end);
    }

    function getFundQuarters(quarters: TQuoter[]) {
      return quarters.reduce((map, q) => ({ ...map, [q.label]: [] }), {});
    }

    rawMetricsData?.forEach((rawMetric) => {
      rawMetric.transactions.forEach((transaction) => {
        const transactionQuarters = getTransactionQuarters(transaction, last12Quarters);
        if (!transactionQuarters?.length) {
          return;
        }

        transactionQuarters.forEach((quarter) => {
          if (!dataset[quarter.label]) {
            dataset[quarter.label] = [];
          }

          dataset[quarter.label].push(transaction);
        });
      });
    });

    const fundQuarterXirr: Record<string, number | null> = {};
    last12Quarters.forEach((quarter) => {
      const quarterTransactions = dataset[quarter.label] ?? [];
      // A transaction's currentInvestment is its share of FMV; pooled it is the
      // quarter's terminal value, pinned to the quarter end.
      const fmv = quarterTransactions.reduce(
        (sum, transaction) => sum + (transaction.currentInvestment ?? 0),
        0
      );

      fundQuarterXirr[quarter.label] = calculatePoolIrr(
        transactionsToCashflows(quarterTransactions),
        fmv,
        startOfDay(quarter.end)
      );
    });

    const chartData = Object.keys(fundQuarterXirr).map((quarter) => ({
      value: fundQuarterXirr[quarter],
      period: quarter,
    }));

    return mapLineChartData(chartData);
  }, [last12Quarters, rawMetricsData]);

  return useMemo(() => ({ data, last12Quarters }), [data, last12Quarters]);
}
